import { useState } from "react";
import { AlertTriangle, Eye, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const daysLeft = (iso) => Math.round((new Date(`${iso}T00:00:00`) - new Date(`${localDay()}T00:00:00`)) / 86400000);
const todayKey = () => localDay();

export default function TenantBanners() {
  const { user, exitImpersonation } = useAuth();
  const [hidden, setHidden] = useState(() => localStorage.getItem("pvc_reminder_hidden") === todayKey());
  if (!user) return null;

  const left = user.expires_at ? daysLeft(user.expires_at) : null;
  const expiring = left !== null && left >= 0 && left <= 7;
  const usage = user.card_limit && user.cards_used != null ? user.cards_used / user.card_limit : 0;
  const nearLimit = usage >= 0.9;
  const dismiss = () => { localStorage.setItem("pvc_reminder_hidden", todayKey()); setHidden(true); };
  const date = user.expires_at && new Date(`${user.expires_at}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      {user.impersonated && (
        <div className="bg-rose-600 text-white text-xs px-5 py-2 flex items-center justify-between gap-3" data-testid="impersonation-banner">
          <span className="flex items-center gap-2"><Eye size={14} /> Super admin support mode — you are viewing <b>{user.email}</b>'s account. Changes are saved to this store.</span>
          <button onClick={exitImpersonation} className="px-3 py-1 rounded bg-white/15 hover:bg-white/25 font-semibold" data-testid="exit-impersonation-button">Return to super admin</button>
        </div>
      )}
      {user.is_trial && !user.impersonated && (
        <div className="bg-emerald-500 text-slate-950 text-xs px-5 py-2 flex items-center justify-between gap-3" data-testid="trial-banner">
          <span className="font-medium">Free trial · {user.cards_used ?? 0} of {user.card_limit} cards used · editing and deleting cards unlock when you subscribe.</span>
          <Link to="/billing" className="px-3 py-1 rounded bg-slate-950/15 hover:bg-slate-950/25 font-semibold" data-testid="trial-subscribe-link">Subscribe</Link>
        </div>
      )}
      {!hidden && !user.is_trial && (expiring || nearLimit) && (
        <div className="bg-amber-400 text-slate-950 text-xs px-5 py-2.5 flex items-center justify-between gap-3" data-testid="plan-reminder-banner">
          <span className="flex items-center gap-2 font-medium">
            <AlertTriangle size={14} />
            {expiring && (
              <span data-testid="expiry-reminder-text">
                {left === 0 ? "Your plan expires today" : `Your plan expires in ${left} day${left === 1 ? "" : "s"}`} ({date}). Renew from the Plan page to keep printing without interruption.
              </span>
            )}
            {nearLimit && (
              <span data-testid="limit-reminder-text">
                {expiring && " · "}You've used {user.cards_used} of {user.card_limit} cards{usage >= 1 ? " — limit reached" : ""}.
              </span>
            )}
          </span>
          <button onClick={dismiss} className="p-1 rounded hover:bg-black/10" title="Hide for today" data-testid="plan-reminder-dismiss"><X size={14} /></button>
        </div>
      )}
    </>
  );
}
